import { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { GigiAlert, GigiManualFaq } from '../../../../types/models/Presc'
import { colorTokens } from '../../../../shared/styles/theme'
import { FONT_WEIGHT, typeScale } from '../../../../shared/styles/typography'
import { TabPill } from '../TabPill'
import { Heading } from './Heading'

/**
 * 段の名前。QuickSync の知らせの種別 4「疑義アラート」と同じ語。
 * 店のポータルの設定は「AIガイド生成」と呼んでいて揃っていない（揃えるのは別の変更）。
 */
export const GIGI_HEADING = '疑義アラート'

/**
 * 疑義アラートの段。項目の上に置き、**入れ方の答えがある行は その場で 答え → 原文 → よくある問い まで出す**
 * (ADR-0075)。答えは back が法人の入力マニュアルから付ける (`GigiAlert.manual`)。無い行は薬名と文面だけ。
 * QuickSync の知らせを押して開いた紙も、一覧から開いた紙も、同じ段を読む。
 *
 * 原文は言い換えずに出し、資料名をその下に沈める (02-components/callout-banner.md の「muted 従属行」)。
 * よくある問いは tab-pill (1 つだけ選ばれ、答えがその下の 1 枠に入れ替わる)。
 * 自由入力 (AI でマニュアルを調べる) はマニュアルが届いてから足す。
 */
export const GigiAlerts = ({ alerts }: { alerts: GigiAlert[] }) => {
  if (alerts.length === 0) return null
  return (
    <Box sx={{ mb: 1 }}>
      <Heading>{GIGI_HEADING}</Heading>
      {alerts.map((alert) => (
        <GigiAlertRow key={alert.id} alert={alert} />
      ))}
    </Box>
  )
}

const Quote = ({ text, source }: { text: string; source: string }) => (
  <>
    <Typography
      sx={{ ...typeScale.bodySm, color: colorTokens.fg.muted, mt: 0.5 }}
    >
      {text}
    </Typography>
    <Typography
      sx={{ ...typeScale.micro, color: colorTokens.fg.faint, mt: 0.25 }}
    >
      {source}
    </Typography>
  </>
)

const GigiAlertRow = ({ alert }: { alert: GigiAlert }) => {
  const [asked, setAsked] = useState<GigiManualFaq | null>(null)
  const medicine = alert.medicine_names.filter(Boolean).join('・')
  const manual = alert.manual
  const faq = manual?.faq ?? []

  if (!manual) {
    return (
      <Typography
        sx={{ ...typeScale.bodySm, color: colorTokens.fg.default, py: 0.5 }}
      >
        {medicine ? `${medicine}: ${alert.alert_text}` : alert.alert_text}
      </Typography>
    )
  }

  return (
    <Box sx={{ py: 0.75 }}>
      {medicine && (
        <Typography sx={{ ...typeScale.bodySm, color: colorTokens.fg.default }}>
          {medicine}
        </Typography>
      )}
      <Typography
        sx={{
          ...typeScale.bodySm,
          fontWeight: FONT_WEIGHT.medium,
          color: colorTokens.fg.default,
          mt: 0.25,
        }}
      >
        {`→ ${manual.answer}`}
        <Box
          component="span"
          sx={{
            ...typeScale.micro,
            fontWeight: FONT_WEIGHT.regular,
            color: colorTokens.fg.muted,
            ml: 1,
          }}
        >
          {manual.source}
        </Box>
      </Typography>
      {manual.quote && <Quote {...manual.quote} />}
      {faq.length > 0 && (
        <>
          <Box
            role="tablist"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}
          >
            {faq.map((item) => (
              <TabPill
                key={item.question}
                selected={asked?.question === item.question}
                onClick={() => setAsked(item)}
              >
                {item.question}
              </TabPill>
            ))}
          </Box>
          {/* 読み上げの枠は先に置き、中身だけ入れ替える (枠ごと挿入すると支援技術が黙る) */}
          <Box role="tabpanel" aria-live="polite" sx={{ mt: asked ? 1 : 0 }}>
            {asked && (
              <>
                <Typography
                  sx={{
                    ...typeScale.bodySm,
                    fontWeight: FONT_WEIGHT.medium,
                    color: colorTokens.fg.default,
                  }}
                >
                  {asked.answer}
                </Typography>
                {asked.quote && <Quote {...asked.quote} />}
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}
